import { apiClient } from './client';
import type { ChokepointId } from '../lib/chokepoints';

// ── Types ────────────────────────────────────────────────────────────
// Mirrors backend/schemas/vessel_positions.py::MaritimeAnomalyEnvelope.
// Cycle 101.1 sprint-1 Task 1.5 adds the degradation tier fields:
// `last_successful_fetch_at`, `fetch_error_rate`, `degraded`.

export type MaritimeAnomalySeverity = 'routine' | 'notable' | 'significant' | 'critical';

export interface MaritimeAnomalyEnvelope {
  chokepoint: ChokepointId;
  as_of: string;
  anomaly_count: number;
  severity: MaritimeAnomalySeverity;
  notes: string;
  fidelity: 'composite';
  source_group: 'maritime_ais';
  last_successful_fetch_at: string | null;
  fetch_error_rate: number;
  degraded: boolean;
}

// ── Cycle-106 sprint-3 FR-3.0 — ships proxy types ───────────────────
// Mirrors backend/schemas/maritime_proxy.py shipped in cycle-106 sprint-1.

/** Backend `RegionLiteral` (TitleCase + hyphen). */
export type MaritimeRegion =
  | 'Hormuz'
  | 'Suez'
  | 'Malacca'
  | 'Bab-el-Mandeb'
  | 'Bosphorus'
  | 'Panama';

/** Translates the existing `ChokepointId` (lowercase, snake_case for Bab-el-Mandeb)
 * into the backend `RegionLiteral` shape. Single source of truth for the
 * frontend → backend region naming bridge. */
export const CHOKEPOINT_TO_BACKEND_REGION: Record<ChokepointId, MaritimeRegion> = {
  hormuz: 'Hormuz',
  suez: 'Suez',
  malacca: 'Malacca',
  bab_el_mandeb: 'Bab-el-Mandeb',
  bosphorus: 'Bosphorus',
  panama: 'Panama',
};

/** Canonical ship schema — mirrors `backend/schemas/maritime_proxy.py:MaritimeShip`. */
export interface MaritimeShip {
  name: string;
  type: string;
  lat: number;
  lng: number;
  country: string | null;
  desc: string | null;
  wiki: string | null;
  source: string | null;
  estimated: boolean;
  mmsi: number | null;
  callsign: string | null;
  imo: number | null;
  heading: number | null;
  sog: number | null;
}

export interface MaritimeShipsResponse {
  ships: MaritimeShip[];
  count: number;
  region: MaritimeRegion | null;
  cached: boolean;
  fetched_at: string;
}

export interface GetShipsParams {
  /** 1 ≤ limit ≤ 500 (backend FastAPI Query bounds). Default 100 server-side. */
  limit?: number;
  /** Optional chokepoint filter. */
  region?: MaritimeRegion;
}

/** Typed error for the 503 + `Upstream-Unavailable: 1` contract from the proxy.
 *  Lets `useMaritimeShips` surface a graceful "Maritime feed unavailable"
 *  chip instead of crashing the canvas (AC-3.0.e). */
export class MaritimeUpstreamUnavailableError extends Error {
  readonly code = 'upstream_unavailable' as const;
  constructor(message = 'PULSE upstream unavailable') {
    super(message);
    this.name = 'MaritimeUpstreamUnavailableError';
    Object.setPrototypeOf(this, MaritimeUpstreamUnavailableError.prototype);
  }
}

// ── API ──────────────────────────────────────────────────────────────

export const maritimeApi = {
  async getAnomaly(chokepoint: ChokepointId): Promise<MaritimeAnomalyEnvelope> {
    const { data } = await apiClient.get('/api/v1/maritime/anomaly', {
      params: { chokepoint },
    });
    return data;
  },

  async getShips(params: GetShipsParams = {}): Promise<MaritimeShipsResponse> {
    const wire: Record<string, unknown> = {};
    if (params.limit !== undefined) wire.limit = params.limit;
    if (params.region !== undefined) wire.region = params.region;
    try {
      const { data } = await apiClient.get<MaritimeShipsResponse>(
        '/api/v1/maritime/ships',
        { params: wire },
      );
      return data;
    } catch (error: unknown) {
      // Detect the 503 + Upstream-Unavailable contract from the FR-1.0 proxy.
      // axios surfaces `error.response.status` + lowercased headers on non-2xx.
      const ax = error as {
        response?: { status?: number; headers?: Record<string, string> };
      };
      const status = ax?.response?.status;
      const headers = ax?.response?.headers ?? {};
      const upstreamHeader =
        headers['upstream-unavailable'] ?? headers['Upstream-Unavailable'];
      if (status === 503 && upstreamHeader === '1') {
        throw new MaritimeUpstreamUnavailableError();
      }
      throw error;
    }
  },
};
